import cloneDeep from 'lodash/cloneDeep';

import {
  FormData,
  UserInfoData,
  FormStructure,
  Topics,
  Topic,
  Fields,
  Field,
  SelfHelpLink,
  MarketFilters,
} from 'etc/types';

import {CONTACT_FORM_STRUCTURE} from './contactFormStructure';

// If we need to do bigger changes than this, we should use lodash.cloneDeep or similar to avoid mutation
const PRIVACY_FORM_STRUCTURE: FormStructure = {
  ...CONTACT_FORM_STRUCTURE,
  topics: CONTACT_FORM_STRUCTURE.topics.privacy.topics || {},
  total_steps: 3,
};

export default class FormConfig {
  formStructure: FormStructure;

  constructor(formStructure: FormStructure) {
    this.formStructure = formStructure;
  }

  getRootTopics(market: string) {
    const topics = this.formStructure.topics;
    return FormConfig.filterTopics(topics, market);
  }

  getTopic(topicName: string) {
    return this.formStructure.topics && this.formStructure.topics[topicName];
  }

  getSubtopics(topicName: string, market: string) {
    const topic = this.getTopic(topicName);
    return topic && topic.topics && FormConfig.filterTopics(topic.topics, market);
  }

  getSubtopic(topicName: string, subtopic: string) {
    const topic = this.getTopic(topicName);
    return topic && topic.topics && topic.topics[subtopic];
  }

  isEnglishMarket(market: string) {
    return this.formStructure.english_markets && this.formStructure.english_markets.includes(market);
  }

  // Adds together default fields and custom fields, if available for this topic
  getAllFormFields(topic: Topic) {
    const allFormFields: Fields = {};

    this.formStructure.form.always_included_fields.forEach(key => {
      allFormFields[key] = {
        ...this.formStructure.form.fields[key],
        name: key,
      };
    });

    if (topic.form && topic.form.fields) {
      Object.keys(topic.form.fields).forEach(key => {
        allFormFields[key] = {
          ...this.formStructure.form.fields[key],
          ...topic.form!.fields[key],
          name: key,
        };
      });
    }

    return allFormFields;
  }

  // Extracts all fields that should be active (depending on logged in, depends_on)
  // This includes updating data based on possible overrides
  getActiveFormFields(
    topic: Topic,
    formData: FormData,
    userData: UserInfoData | null,
    market: string,
    overrides: string[] = []
  ): Field[] {
    const allFormFields = this.getAllFormFields(topic);

    const activeFormFields: Fields = {};

    Object.keys(allFormFields).forEach(key => {
      const field = FormConfig.transformFieldForOverrides(allFormFields[key], overrides);

      if (!FormConfig.isFieldVisible(field, userData)) {
        return;
      }

      // Filter fields based on their depends_on config
      const dependsOn = field.depends_on;
      if (dependsOn) {
        const fieldVisible = FormConfig.isFieldVisible(allFormFields[dependsOn.field], userData);

        if (
          fieldVisible &&
          !dependsOn.for_values.includes(formData[dependsOn.field] && formData[dependsOn.field].value)
        ) {
          return;
        }
      }

      activeFormFields[key] = field;
    });

    return Object.keys(activeFormFields)
      .map(key => ({...activeFormFields[key], name: key}))
      .filter(field => FormConfig.filterForMarket(field, market))
      .sort(({order: a = 0}, {order: b = 0}) => a - b);
  }

  getAllFormArticleIds() {
    // A set will not hold any duplicates (article IDs are duplicated over the cf)
    const articleIds = new Set<string>();

    const addArticlesToSet = (articles: SelfHelpLink[] = []) => {
      articles.forEach(article => articleIds.add(article.id));
    };

    Object.keys(this.formStructure.topics).forEach(topic => {
      addArticlesToSet(this.formStructure.topics[topic].articles);

      if (!this.formStructure.topics[topic].topics) return;

      Object.keys(this.formStructure.topics[topic].topics!).forEach(subtopic => {
        addArticlesToSet(this.formStructure.topics[topic].topics![subtopic].articles);
      });
    });

    return Array.from(articleIds);
  }

  getDefaultStepCount() {
    return this.formStructure.total_steps;
  }

  static transformFieldForOverrides(field: Field, activeOverrides: string[] = []) {
    if (field.overrides && activeOverrides.length) {
      const transformedField = cloneDeep(field);

      field.overrides.forEach(override => {
        if (override.match_all) {
          for (const value of override.match_all) {
            if (!activeOverrides.includes(value)) return;
          }
        }

        if (override.match_any) {
          const match = override.match_any.find(value => activeOverrides.includes(value));
          if (!match) return;
        }

        Object.assign(transformedField, override.data);
      });
      return transformedField;
    }
    return field;
  }

  static filterTopics(topics: Topics, market: string) {
    return Object.keys(topics).filter(key => FormConfig.filterForMarket(topics[key], market));
  }

  static filterForMarket(data: MarketFilters = {}, market: string) {
    if (data.markets && !data.markets.includes(market)) {
      return false;
    }

    if (data.excluded_markets && data.excluded_markets.includes(market)) {
      return false;
    }
    return true;
  }

  static isFieldVisible(field: Field, userData: UserInfoData | null) {
    // Not logged in? All fields are visible
    if (!userData) {
      return true;
    }

    // Ask for email if the user is logged in but does not have an email
    if (field.name === 'contact_email' && !userData.email) {
      return true;
    }

    // If logged in, we check for the specific flag visible_logged_in:false
    return field.visible_logged_in !== false;
  }

  static isFormValid(formData: FormData) {
    return !Object.keys(formData).find(key => !formData[key].valid);
  }
}

export const contactFormConfig = new FormConfig(CONTACT_FORM_STRUCTURE);
export const privacyFormConfig = new FormConfig(PRIVACY_FORM_STRUCTURE);
